import React, { useState, useEffect, useCallback, memo } from 'react';
import { Map } from 'immutable';
import { connect } from 'react-redux';
import classNames from 'classnames';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import { getChanged, getIn, isDeepChanged } from '../../utils/ChangeSpy';
import getOptionValue from '../../utils/getOptionValue';
import { findUsers, changeField, clearSuggest, makeContactLP } from '../../actions/contact/contactLP';
import { changeContactInfoField, closeValidationError } from '../../actions/contactDetail';

import AutoComplete from '../AutoComplete';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { useRedirectForceContext } from '../../contexts/RedirectForceContext';
import { usePopupContext } from '../../contexts/PopupContext';

const onGetValue = suggestion => suggestion?.value ?? '';

const AddLpContactPopupContainer = ({
  children,
  contact,
  contactInfo,
  lp,
  user,
  inputErrors,
  findUsers,
  changeField,
  changeContactInfoField,
  closeValidationError,
  makeContactLP,
}) => {
  const redirectForce = useRedirectForceContext();
  const { openPopup } = usePopupContext();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const module = {
      username: user.get('module') || '',
      id: user.get('moduleId') || 0,
    };

    changeField({ name: 'suggestRelation', value: module.username });
    changeField({ name: 'lpPrimaryRelId', value: module.id });
  }, [user, changeField]);

  const onErrorClose = useCallback(
    (event, field) => {
      closeValidationError({ field });
    },
    [closeValidationError],
  );

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      return changeContactInfoField({ name, value });
    },
    [changeContactInfoField],
  );

  const handleLpChange = useCallback(
    event => {
      const { name, value } = event.target;

      return changeField({ name, value });
    },
    [changeField],
  );

  const handleFindEmail = useCallback(() => {
    openPopup('EmailFinderPopup', {
      contact,
      contactInfo,
      lp,
      user,
      inputErrors,
      reopenPopupName: 'AddLpContactPopup',
    });
  }, [openPopup, contact, contactInfo, lp, user, inputErrors]);

  const handleCreate = useCallback(() => {
    if (contact.get('isValid')) {
      const id = contact.get('id') || 'new';
      const body = getChanged(contact);
      const prefix = getOptionValue(contact.get('prefix'));
      const suffix = getOptionValue(contact.get('suffix'));
      const lpFields = getChanged(lp);

      const afterSuccess = ({ response }) => {
        setTimeout(() => {
          redirectForce(`/contact/${response.id}/lp`);
        }, 0);
      };

      const afterError = () => {
        setSubmitting(false);
      };

      setSubmitting(true);
      makeContactLP({ ...body, ...lpFields, prefix, suffix, id }, afterSuccess, afterError);
    }
  }, [contact, lp, makeContactLP, redirectForce]);

  const handleFetchUsers = useCallback(
    ({ value }) => {
      findUsers({ filter: value });
    },
    [findUsers],
  );

  const onUpdateSuggestions = useCallback(
    (suggestionName, param) => {
      if ('text' in param) {
        changeField({ name: suggestionName, value: param.text });
      }

      if ('suggestions' in param) {
        changeField({ name: 'suggests', value: param.suggestions });
      }
    },
    [changeField],
  );

  const onUpdateRelation = useCallback(param => onUpdateSuggestions('suggestRelation', param), [onUpdateSuggestions]);

  const onSelectRelation = useCallback(
    (event, { suggestion }) => {
      changeField({ name: 'suggestRelation', value: suggestion.text });
    },
    [changeField],
  );

  const handleChangeSuggestion = useCallback(
    object => {
      if (typeof object.value === 'string') {
        const { value } = object;
        const nextValue = contactInfo.get('suffixes').find(
          suffix => suffix.get('name') === value,
          null,
          Map({
            name: value || '',
            value: value || '',
          }),
        );

        changeContactInfoField({ name: 'suffix', value: nextValue });
      }
    },
    [contactInfo, changeContactInfoField],
  );

  const suggestRelation = {
    onFetch: handleFetchUsers,
    onUpdate: onUpdateRelation,
    getValue: onGetValue,
    onSelect: onSelectRelation,
    value: lp.get('suggestRelation', ''),
  };

  const suffixValue = getIn(contactInfo, 'suffix').get('name');
  const regExp = new RegExp(String(suffixValue).replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1'), 'i');
  const suffixClassName = classNames('form-control input-sm', {
    changed: isDeepChanged(contactInfo.get('suffix')),
  });
  const suffixSuggestions = getIn(contactInfo, 'suffixes')
    .filter(suffix => regExp.test(suffix.get('name')))
    .map(suffix => ({
      id: suffix.get('value'),
      text: suffix.get('name'),
    }));

  const suggestSuffix = (
    <AutoComplete
      idValue={suffixValue}
      inputProps={{ className: suffixClassName, name: 'suffix' }}
      loading={false}
      name="suffix"
      onChange={handleChangeSuggestion}
      onFind={args => {
        setTimeout(
          () =>
            args.afterSuccess({
              response: {
                data: getIn(contactInfo, 'suffixes')
                  .filter(suffix => regExp.test(suffix.get('name')))
                  .map(suffix => ({
                    id: suffix.get('value'),
                    text: suffix.get('name'),
                  })),
              },
            }),
          0,
        );
      }}
      suggestions={suffixSuggestions}
      suggestionsName="suffixes"
      value={suffixValue}
      suggestIfEmpty
    />
  );

  const isValid = inputErrors && inputErrors.size === 0;

  return (
    <PrettyErrorProvider inputErrors={inputErrors} onErrorClose={onErrorClose}>
      <div>
        <Popups.AddLpContactPopup
          contact={contact}
          isValid={isValid}
          lp={lp}
          onChange={handleChange}
          onCreate={handleCreate}
          onFindEmail={handleFindEmail}
          onLpChange={handleLpChange}
          submitting={submitting}
          suggestRelation={suggestRelation}
          suggestSuffix={suggestSuffix}
        />
        {children}
      </div>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    inputErrors: state.contact.info.get('inputErrors'),
    contact: state.contact.info,
    contactInfo: state.contact.info,
    lp: state.contact.lp,
    user: state.auth.get('user', null),
  };
}

export { AddLpContactPopupContainer };

const Connected = connect(
  mapStateToProps,
  {
    findUsers,
    changeContactInfoField,
    clearSuggest,
    makeContactLP,
    changeField,
    closeValidationError,
  },
  mergeProps,
  connectOptions,
)(memo(AddLpContactPopupContainer));

Connected.closable = false;

export default Connected;
